import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface OfflineDataOptions {
  key: string;
  syncInterval?: number;
  maxAge?: number;
}

interface CachedData<T> {
  data: T;
  timestamp: number;
  version: string;
}

export const useOfflineData = <T>(
  fetchFunction: () => Promise<T>,
  options: OfflineDataOptions
) => {
  const { key, syncInterval = 5 * 60 * 1000, maxAge = 24 * 60 * 60 * 1000 } = options;
  
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Gerenciar status de conexão
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Salvar dados no localStorage
  const saveToCache = useCallback((data: T) => {
    try {
      const cachedData: CachedData<T> = {
        data,
        timestamp: Date.now(),
        version: '1.0'
      };
      localStorage.setItem(`offline_${key}`, JSON.stringify(cachedData));
    } catch (error) {
      console.warn('Erro ao salvar dados offline:', error);
    }
  }, [key]);

  // Carregar dados do localStorage
  const loadFromCache = useCallback((): T | null => {
    try {
      const cached = localStorage.getItem(`offline_${key}`);
      if (!cached) return null;

      const cachedData: CachedData<T> = JSON.parse(cached);
      const age = Date.now() - cachedData.timestamp;

      if (age > maxAge) {
        localStorage.removeItem(`offline_${key}`);
        return null;
      }

      setLastSync(new Date(cachedData.timestamp));
      return cachedData.data;
    } catch (error) {
      console.warn('Erro ao carregar dados offline:', error);
      return null;
    }
  }, [key, maxAge]);

  // Buscar dados online
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!isOnline && !forceRefresh) {
      const cachedData = loadFromCache();
      if (cachedData) {
        setData(cachedData);
        setIsLoading(false);
        return cachedData;
      }
    }

    try {
      setError(null);
      const freshData = await fetchFunction();
      setData(freshData);
      saveToCache(freshData);
      setLastSync(new Date());
      setIsLoading(false);
      return freshData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      
      // Tentar carregar dados do cache em caso de erro
      const cachedData = loadFromCache();
      if (cachedData) {
        setData(cachedData);
      }
      setIsLoading(false);
      throw err;
    }
  }, [isOnline, fetchFunction, loadFromCache, saveToCache]);

  // Sincronização automática
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(() => {
      fetchData().catch(console.error);
    }, syncInterval);

    return () => clearInterval(interval);
  }, [isOnline, syncInterval, fetchData]);

  // Carregar dados iniciais
  useEffect(() => {
    const loadInitialData = async () => {
      // Primeiro, tentar carregar do cache
      const cachedData = loadFromCache();
      if (cachedData) {
        setData(cachedData);
        setIsLoading(false);
      }

      // Depois, tentar buscar dados frescos se online
      if (isOnline) {
        try {
          await fetchData();
        } catch (error) {
          // Se falhar e não temos dados em cache, manter o erro
          if (!cachedData) {
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Sincronizar quando voltar online
  useEffect(() => {
    if (isOnline && data) {
      fetchData().catch(console.error);
    }
  }, [isOnline]);

  const refresh = useCallback(() => {
    setIsLoading(true);
    return fetchData(true);
  }, [fetchData]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(`offline_${key}`);
    setData(null);
    setLastSync(null);
  }, [key]);

  return {
    data,
    isLoading,
    isOnline,
    lastSync,
    error,
    refresh,
    clearCache
  };
};

// Hook para gerenciar múltiplas entidades offline
export const useOfflineStorage = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const saveOfflineAction = useCallback((action: any) => {
    try {
      const actions = JSON.parse(localStorage.getItem('offline_actions') || '[]');
      actions.push({
        ...action,
        id: Date.now().toString(),
        timestamp: Date.now()
      });
      localStorage.setItem('offline_actions', JSON.stringify(actions));
    } catch (error) {
      console.warn('Erro ao salvar ação offline:', error);
    }
  }, []);

  const getOfflineActions = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem('offline_actions') || '[]');
    } catch (error) {
      console.warn('Erro ao carregar ações offline:', error);
      return [];
    }
  }, []);

  const syncOfflineActions = useCallback(async () => {
    if (!isOnline) return;

    const actions = getOfflineActions();
    if (actions.length === 0) return;

    try {
      for (const action of actions) {
        // Aqui você implementaria a lógica específica para cada tipo de ação
        switch (action.type) {
          case 'CREATE_CASE':
            await supabase.from('cases').insert(action.data);
            break;
          case 'UPDATE_CASE':
            await supabase.from('cases').update(action.data).eq('id', action.id);
            break;
          case 'CREATE_CLIENT':
            await supabase.from('clients').insert(action.data);
            break;
          // Adicionar mais tipos conforme necessário
        }
      }
      
      // Limpar ações sincronizadas
      localStorage.removeItem('offline_actions');
    } catch (error) {
      console.error('Erro ao sincronizar ações offline:', error);
    }
  }, [isOnline, getOfflineActions]);

  // Sincronizar quando voltar online
  useEffect(() => {
    if (isOnline) {
      syncOfflineActions();
    }
  }, [isOnline, syncOfflineActions]);

  return {
    isOnline,
    saveOfflineAction,
    getOfflineActions,
    syncOfflineActions
  };
};

// Hook para dados críticos específicos
export const useCriticalOfflineData = () => {
  // Casos
  const cases = useOfflineData(
    async () => {
      const { data, error } = await supabase
        .from('cases')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data || [];
    },
    { key: 'cases', syncInterval: 10 * 60 * 1000 } // 10 minutos
  );

  // Clientes
  const clients = useOfflineData(
    async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data || [];
    },
    { key: 'clients', syncInterval: 15 * 60 * 1000 } // 15 minutos
  );

  // Compromissos do dia
  const todayAppointments = useOfflineData(
    async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .gte('date', today)
        .lte('date', today + 'T23:59:59')
        .order('time', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    { key: 'today_appointments', syncInterval: 5 * 60 * 1000 } // 5 minutos
  );

  return {
    cases,
    clients,
    todayAppointments
  };
};